/**
 * Ecommerce dataLayer helpers (VP-3933).
 *
 * Mirrors the production contract implemented by the legacy FED module
 * `VP.googleAnalytics` (vplates-fed-creative-exchange/src/assets/scripts-src/modules/googleAnalytics.js)
 * so the existing GTM / GA4 / Floodlight tags keep working unchanged.
 *
 * Contract summary
 * ----------------
 * - Every ecommerce event is preceded by `{ ecommerce: null }` (GA4 recommended reset).
 *   `select_availability` is the exception — production pushes it without the reset.
 * - Item shape (index is always 0, single-line cart):
 *     item_id         legacy product id, e.g. "Car_Custom_New" (see prodProductId)
 *     item_name       product display name (e.g. "Custom")
 *     item_brand      product type: "create" | "restyle" | "giftvoucher"
 *     item_category   vehicle type: "car" | "motorcycle"
 *     item_category2  VariantName, e.g. "Car_Custom_1N2L1N2L_Black_Tuxedo__OOE"
 *     item_category3  colour name
 *     item_category4  size name
 *     item_category5  plate combination
 *     quantity        1 (omitted on select_item, as in production)
 *     price           string with 2dp, e.g. "710.00"
 * - Checkout events (begin_checkout, add_shipping_info, add_payment_info, payment) carry
 *   `value: "710.00 AUD"` (string, as production) plus `ecommerce.currency` / `transaction_id`.
 *
 * Every public function is wrapped so analytics can never break a purchase flow.
 */

export const PRODUCT_TYPE = Object.freeze({
  CREATE: 'create',
  RESTYLE: 'restyle',
  GIFT: 'giftvoucher',
});

const CURRENCY = 'AUD';
const PAYMENT_TRACKED_KEY = 'gaPaymentTracked';

const safe = (name, fn) => (...args) => {
  try {
    return fn(...args);
  } catch (err) {
    // Never let tracking break the journey.
    console.warn(`[analytics] ${name} failed`, err);
    return undefined;
  }
};

const push = (payload) => {
  if (typeof window === 'undefined') return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(payload);
};

const pushEcommerce = (payload) => {
  push({ ecommerce: null });
  push(payload);
};

/** "710" | 710 | "1,250.5" -> "710.00" | "1250.50" */
export const formatPrice = (amount) => {
  const n = parseFloat(String(amount ?? '').replace(/,/g, ''));
  return Number.isFinite(n) ? n.toFixed(2) : '0.00';
};

const formatValue = (amount) => `${formatPrice(amount)} ${CURRENCY}`;

/**
 * Size name in the production cart format (cart line `SizeName`), NOT the on-screen
 * CMS label ("2 Standard", "1 Standard & 1 Slimline"):
 *   OOE      -> "One of each (1 Standard & 1 Slimline)" / "One of each (1 Euro & 1 Mini Euro)"
 *   ST / SL  -> variant SizeInfo.SizeDescription, e.g. "Standard 372mm (w) 132mm (h)"
 * The OOE variant's own SizeInfo describes only one plate, so OOE is built explicitly.
 */
export const prodSizeName = (sizeCode, sizeDescription, productName) => {
  if (String(sizeCode || '').toUpperCase() === 'OOE') {
    return /euro/i.test(productName || '')
      ? 'One of each (1 Euro & 1 Mini Euro)'
      : 'One of each (1 Standard & 1 Slimline)';
  }
  return sizeDescription || sizeCode || null;
};

const toId = (v) => (v === null || v === undefined || v === '' ? null : String(v));

const JOURNEY_SUFFIX = Object.freeze({
  [PRODUCT_TYPE.CREATE]: 'New',
  [PRODUCT_TYPE.RESTYLE]: 'Restyle',
});

/**
 * Legacy Sitecore product id, as production sent in `item_id`:
 *   `{Vehicle}_{ProductName with spaces -> _}_{New|Restyle}`
 *   e.g. "Car_Custom_New", "Car_Melbourne_Storm_Upgrade_Restyle",
 *        "Motorcycle_Custom_Motorcycle_New"
 * The replatformed catalogue only has a numeric ProductId, so the legacy id is
 * rebuilt from ProductName + vehicle type + journey. Returns null if any part is missing.
 */
export const prodProductId = (productName, vehicleType, productType) => {
  const suffix = JOURNEY_SUFFIX[productType];
  const name = String(productName || '').trim().replace(/\s+/g, '_');
  const vt = String(vehicleType || '').trim().toLowerCase();
  const vehicle = vt === 'car' || vt === '1' ? 'Car'
    : vt === 'motorcycle' || vt === '2' ? 'Motorcycle'
    : null;
  if (!suffix || !name || !vehicle) return null;
  return `${vehicle}_${name}_${suffix}`;
};

/**
 * Build a plate item from a plain descriptor.
 * @param {object} d
 * @param {boolean} [withQuantity=true] production omits `quantity` on select_item only
 */
export const buildPlateItem = (d, withQuantity = true) => {
  const item = {
    index: 0,
    // Prod-style legacy id; falls back to the numeric catalogue ProductId if it can't be built.
    item_id: prodProductId(d.productName, d.vehicleType, d.productType) ?? toId(d.productId),
    item_name: d.productName || null,
    item_brand: d.productType,
    item_category: d.vehicleType || null,
    item_category2: toId(d.variantKey),
    item_category3: d.colour || null,
    item_category4: d.sizeName || null,
    // Prod sends the cart PlateCombo, which is always upper case.
    item_category5: d.combination ? String(d.combination).toUpperCase() : null,
  };
  if (withQuantity) item.quantity = 1;
  item.price = formatPrice(d.price);
  return item;
};

/** Map the `plateOrderData` session object (create / restyle) to an item. */
export const plateItemFromOrder = (orderData, productType) =>
  buildPlateItem({
    // analyticsProductId = numeric catalogue ProductId; productId in session is the
    // card id (`${ProductId}_${variantKey}`) used by later pages, so it is left untouched.
    productId: orderData?.analyticsProductId ?? orderData?.productId,
    productName: orderData?.productName,
    productType,
    vehicleType: orderData?.vehicleType,
    // item_category2 = VariantName (e.g. "Car_Custom_1N2L1N2L_Black_Tuxedo__OOE"); numeric id as fallback.
    variantKey: orderData?.variantName || orderData?.variantId,
    colour: orderData?.variantColour || orderData?.colorName,
    // analyticsSizeName = prod-format size; sizeName is the on-screen label used by later pages.
    sizeName: orderData?.analyticsSizeName ?? orderData?.sizeName,
    combination: orderData?.combination,
    price: orderData?.price,
  });

/** "195.00" | 195 | "$1,000" -> "195" | "1000"; keeps real cents ("50.50" -> "50.5"). */
const amountForId = (amount) => {
  const n = parseFloat(String(amount ?? '').replace(/[$,\s]/g, ''));
  return Number.isFinite(n) && n > 0 ? String(n) : null;
};

/**
 * Gift voucher variant id in the production format `{themeId}_{amount}`,
 * e.g. "Gift_Card_Generic_195". Amount is the voucher face value (no ".00").
 * Falls back to the numeric misc variant Id if the amount is unknown.
 */
export const giftVariantKey = (giftData, amount) => {
  const amt = amountForId(giftData?.voucherAmount) ?? amountForId(amount ?? giftData?.amount);
  if (giftData?.themeId && amt) return `${giftData.themeId}_${amt}`;
  return toId(giftData?.variantId);
};

/** Map the `giftOrderData` session object to an item (matches the prod gift cart line). */
export const giftItemFromOrder = (giftData, amount) => ({
  index: 0,
  item_id: toId(giftData?.themeId),
  item_name: giftData?.themeDisplayName || null,
  item_brand: PRODUCT_TYPE.GIFT,
  item_category: null,
  item_category2: giftVariantKey(giftData, amount),
  item_category3: null,
  item_category4: null,
  item_category5: null,
  quantity: 1,
  price: formatPrice(amount ?? giftData?.amount),
});

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

/**
 * SPA route change. Production was multi-page, so GTM saw a fresh page load per step;
 * in the SPA only the first page loads GTM, so each client-side navigation pushes this
 * event instead. GTM needs a Custom Event trigger on `virtual_page_view` to send GA4 page views.
 */
export const trackVirtualPageView = safe('virtual_page_view', () => {
  const { href, pathname, search } = window.location;
  push({
    event: 'virtual_page_view',
    page_location: href,
    page_path: `${pathname}${search}`,
    page_title: document.title,
  });
});

/** Styles page loaded with ?combination=&vehicleType= (prod: filteredStyles/filteredProducts). */
export const trackSelectAvailability = safe('select_availability', (combination) => {
  if (!combination) return;
  push({ event: 'select_availability', element_text: combination });
});

/** User clicked SELECT STYLE. `item` should be built with withQuantity=false. */
export const trackSelectItem = safe('select_item', (item) => {
  pushEcommerce({
    event: 'select_item',
    value: item.price,
    ecommerce: { items: [item] },
  });
});

/** User clicked GET THIS PLATE (create / restyle). */
export const trackAddToCart = safe('add_to_cart', (item) => {
  pushEcommerce({
    event: 'add_to_cart',
    ecommerce: {
      transaction_id: Date.now(),
      items: [item],
    },
  });
});

/** Gift voucher personalisation submitted (prod: giftPersonalisation.js). */
export const trackGiftAddToCart = safe('add_to_cart (gift)', (giftData) => {
  pushEcommerce({
    event: 'add_to_cart',
    ecommerce: {
      transaction_id: Date.now(),
      items: [
        {
          index: 0,
          item_id: toId(giftData?.themeId),
          item_brand: PRODUCT_TYPE.GIFT,
          item_category2: giftVariantKey(giftData),
          quantity: 1,
        },
      ],
    },
  });
});

const checkoutEvent = (event, item, total, { transactionId, extra } = {}) => {
  pushEcommerce({
    event,
    ...(extra || {}),
    value: formatValue(total),
    ecommerce: {
      currency: CURRENCY,
      transaction_id: transactionId ?? Date.now(),
      items: [item],
    },
  });
};

/** Identification / delivery details page loaded. */
export const trackBeginCheckout = safe('begin_checkout', (item, total) => {
  checkoutEvent('begin_checkout', item, total);
});

/** Checkout (payment) page loaded. */
export const trackAddShippingInfo = safe('add_shipping_info', (item, total) => {
  checkoutEvent('add_shipping_info', item, total);
});

/** User clicked PLACE ORDER. */
export const trackAddPaymentInfo = safe('add_payment_info', (item, total) => {
  checkoutEvent('add_payment_info', item, total, { extra: { event_timeout: 2000 } });
});

const readTrackedOrders = () => {
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(PAYMENT_TRACKED_KEY) || '[]');
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

/**
 * Order placed successfully. Fires at most once per order number.
 * transaction_id = real order number (production used Date.now()).
 */
export const trackPayment = safe('payment', (item, total, orderNumber) => {
  if (orderNumber) {
    const tracked = readTrackedOrders();
    if (tracked.includes(orderNumber)) return;
    window.sessionStorage.setItem(PAYMENT_TRACKED_KEY, JSON.stringify([...tracked, orderNumber].slice(-20)));
  }
  checkoutEvent('payment', item, total, {
    transactionId: orderNumber || Date.now(),
    extra: { event_timeout: 2000 },
  });
});
